/**
 * ISO 3166-1 alpha-2 countries for the registration + billing-profile pickers.
 *
 * The country is not cosmetic: it decides the checkout currency (IN -> INR,
 * everywhere else USD) and which tax rule an invoice carries.
 */
export interface Country {
  code: string
  name: string
}

export const COUNTRIES: Country[] = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'SG', name: 'Singapore' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'IE', name: 'Ireland' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'NP', name: 'Nepal' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'OM', name: 'Oman' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'EG', name: 'Egypt' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'DK', name: 'Denmark' },
  { code: 'NO', name: 'Norway' },
  { code: 'FI', name: 'Finland' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'IL', name: 'Israel' },
  { code: 'TR', name: 'Türkiye' },
]

/**
 * What the country pickers start on. India, because that is where nearly all
 * of the user base is — and locale-guessing was actively worse here: plenty of
 * Indian users browse with an en-US locale, which would silently preselect the
 * United States and bill them in USD. The field is required and visible, so
 * anyone elsewhere picks their own.
 */
export const DEFAULT_COUNTRY = 'IN'

/**
 * Best guess from the browser locale ("en-IN" -> IN). Not used to preselect the
 * pickers any more (see DEFAULT_COUNTRY); kept for callers that want a hint
 * rather than a default.
 */
export function guessCountry(): string {
  try {
    const locale = navigator.language || ''
    const region = locale.split('-')[1]
    if (region && COUNTRIES.some((c) => c.code === region.toUpperCase())) {
      return region.toUpperCase()
    }
  } catch {
    /* a locale we can't read is not worth an error */
  }
  return ''
}

export function countryName(code: string | null | undefined): string {
  if (!code) return ''
  return COUNTRIES.find((c) => c.code === code.toUpperCase())?.name || code
}
